
































































































import type { Entity } from '@getaccept/lib-shared/src/entity/signing-site/types/entity';
import { MergeType } from '@getaccept/lib-shared/src/fields/types/enums/merge-type';
import { SigningMergeTagHelper } from '@getaccept/lib-shared/src/helpers/signing-merge-tag.helper';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { UrlHelper } from '@getaccept/lib-shared/src/helpers/url.helper';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/enums/recipient-status';
import VerifySignature from '@getaccept/lib-shared/src/signing/components/VerifySignature.vue';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import type { Channel } from '@getaccept/pusher';
import type { PropType } from '@vue/composition-api';
import { computed, defineComponent, onMounted, ref, watch } from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import { DocumentService } from '../../../api/documents/services/document.service';
import { abortApollo } from '../../../apollo';
import EidEasyModal from '../../../eid-easy/EidEasyModal.vue';
import { EmailHelper } from '../../../helpers/email.helper';
import { useSessionStore } from '../../../session/store/session.store';
import { useSigningStore } from '../../store/signing.store';
import type { AfterSignStep } from '../../types/enums/after-sign-step';
import { SigningStep as ESigningStep } from '../../types/enums/signing-step';
import type { SigningStep } from '../../types/signing-step';
import { useSignupStore } from '../../../signup/store/signup.store';
import Eid from './Eid.vue';
import SignConfirmation from './SignConfirmation.vue';
import SignEvidence from './SignEvidence.vue';
import VerifyClick from './VerifyClick.vue';
import VerifyQna from './VerifyQna.vue';
import VerifySms from './VerifySms.vue';
import AfterSign from './signup-after-sign/AfterSign.vue';
import NoSignupOption from './signup-after-sign/NoSignupOption.vue';

export default defineComponent({
  components: {
    Eid,
    VerifySignature,
    VerifyQna,
    VerifySms,
    VerifyClick,
    SignEvidence,
    SignConfirmation,
    AfterSign,
    EidEasyModal,
    NoSignupOption,
  },
  props: {
    document: {
      type: Object as PropType<SigningPageDocument>,
      required: true,
    },
    entity: {
      type: Object as PropType<Entity>,
      required: true,
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      required: true,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    authId: {
      type: String,
      required: true,
    },
    step: {
      type: Object as PropType<SigningStep>,
      required: true,
    },
    signButtonText: {
      type: String,
      default: '',
    },
    reviewPending: {
      type: Boolean,
    },
    shouldPopStripe: {
      type: Boolean,
    },
    recipientPusherChannel: {
      type: Object as PropType<Channel>,
      default: undefined,
    },
  },
  setup(props, { emit }) {
    const hasSignedBankID = ref(false);
    const currentCampaign = ref('v3/module/yes');

    const signingStore = useSigningStore();
    const signupStore = useSignupStore();

    const checkVerificationStep = () => signingStore.checkVerificationStep();
    const verifyQna = (answer: string) => signingStore.verifyQna(answer);
    const sendSmsCode = () => signingStore.sendSmsCode();
    const verifySms = (pin: string) => signingStore.verifySms(pin);
    const verifySignature = (signature: { signatureBase30: string; signatureBase64: string }) =>
      signingStore.verifySignature(signature.signatureBase30, signature.signatureBase64);
    const signDocument = (signInitials?: string) => signingStore.signDocument(signInitials);
    const sendFeedback = (feedback: { feedback: string; email: string }) =>
      signupStore.sendFeedback(feedback.feedback, feedback.email);
    const createSignup = (signup: { email: string; campaign: string }) =>
      signupStore.createSignup(signup.email, signup.campaign);
    const setAfterSignStep = (step: AfterSignStep) => signupStore.setAfterSignStep(step);

    const { loading, qna, sms, signature, sign, initials } = storeToRefs(signingStore);
    const { afterSign } = storeToRefs(signupStore);

    const sessionStorage = useSessionStore();
    const { token } = storeToRefs(sessionStorage);

    const loadExternalId = async (): Promise<string> => {
      try {
        return await DocumentService.loadExternalId();
      } catch (e) {
        console.error('Could not load external id', e);
      }
    };

    const redirect = (url: string) => {
      const safeUrl = UrlHelper.getHttpsUrl(url);
      abortApollo();
      window.open(safeUrl, '_self');
    };

    const handleWatchRecipientStatus = (newValue: RecipientStatus) => {
      const shouldRedirect = newValue === RecipientStatus.Signed && !!props.entity.signRedirect;
      if (!shouldRedirect) {
        return;
      }

      setTimeout(async () => {
        let { signRedirect } = props.entity;
        const tags = SigningMergeTagHelper.findTags(signRedirect);
        if (!tags.length) {
          redirect(signRedirect);
        }
        let externalId = '';
        if (tags.includes(`{{${MergeType.ExternalId}}}`)) {
          externalId = await loadExternalId();
        }
        signRedirect = SigningMergeTagHelper.replaceTags(signRedirect, {
          recipient: props.recipient,
          document: { ...props.document, externalId },
          entity: props.entity,
        });
        redirect(signRedirect);
      }, 300);
    };

    watch(() => props.recipient.status, handleWatchRecipientStatus);

    const showConfirmWindow = computed(
      () => !sign.value.isVerifying && props.recipient.status === RecipientStatus.Signed
    );

    const qesEnabled = computed(() => props.document?.settings?.qualifiedElectronicSignature);

    const showClickToSign = computed(
      () =>
        props.step.type === ESigningStep.Click &&
        !sign.value.isVerifying &&
        props.recipient.status !== RecipientStatus.Signed
    );

    const showConfirmation = computed(
      () => props.recipient.status === RecipientStatus.Signed && !sign.value.isVerifying
    );

    const showEid = computed(() => props.step.type === ESigningStep.Eid && !hasSignedBankID.value);

    const showSignupAfterSign = computed(() => {
      if (props.entity.signRedirect) {
        return false;
      }
      if (!props.sender.email && !props.recipient.email) {
        return false;
      }
      return (
        EmailHelper.getEmailDomain(props.sender.email) !==
        EmailHelper.getEmailDomain(props.recipient.email)
      );
    });

    const signText = computed(() =>
      t('confirm-signature_v2', {
        recipientName: recipientName.value,
        documentName: props.document.name,
      })
    );

    const recipientName = computed(() => {
      if (props.recipient.fullName) {
        return props.recipient.fullName;
      }
      if (props.recipient.firstName || props.recipient.lastName) {
        return `${props.recipient.firstName} ${props.recipient.lastName}`;
      }

      return props.recipient.email;
    });

    const handleVerifySignature = ({
      signatureBase30,
      signatureBase64,
    }: {
      signatureBase30: string;
      signatureBase64: string;
    }) => {
      if (props.reviewPending) {
        closeAndShowReviewModal();
        return;
      }
      verifySignature({ signatureBase30, signatureBase64 });
    };

    const handleSignDocumentEid = () => {
      if (props.reviewPending) {
        closeAndShowReviewModal();
        return;
      }
      hasSignedBankID.value = true;
      if (props.document.isSigningInitials) {
        checkVerificationStep();
      } else {
        signDocument();
      }
    };

    const handleVerifyClick = (initials: string) => {
      if (props.reviewPending) {
        closeAndShowReviewModal();
        return;
      }
      signDocument(initials);
    };

    const closeAndShowReviewModal = () => {
      handleClose();
      emit('showReviewModal');
    };

    const handleClose = () => {
      if (props.reviewPending) {
        emit('showReviewModal');
      }
      if (showEid.value) {
        emit('cancelBankId');
      }
      emit('close');
    };

    const handleSendFeedback = (feedback: string, email: string) => {
      sendFeedback({ feedback, email });
    };

    const handleSignup = (email: string) => {
      createSignup({ email, campaign: currentCampaign.value });
    };

    const handleAfterSignStep = (step: AfterSignStep) => {
      setAfterSignStep(step);
    };

    onMounted(() => {
      if (!showEid.value) {
        checkVerificationStep();
      }
    });

    return {
      ESigningStep,
      RecipientStatus,
      hasSignedBankID,
      showConfirmWindow,
      showClickToSign,
      showConfirmation,
      showEid,
      showSignupAfterSign,
      signText,
      recipientName,
      loading,
      qna,
      sms,
      signature,
      sign,
      afterSign,
      initials,
      token,
      qesEnabled,
      handleVerifySignature,
      handleSignDocumentEid,
      handleVerifyClick,
      handleClose,
      handleSendFeedback,
      handleSignup,
      handleAfterSignStep,
      handleWatchRecipientStatus,
      verifyQna,
      sendSmsCode,
      verifySms,
      verifySignature,
      signDocument,
      sendFeedback,
      createSignup,
      checkVerificationStep,
      setAfterSignStep,
      closeAndShowReviewModal,
    };
  },
});
